// «Editar ficha»: los datos de la agencia que el Admin de la app edita
// (views/admin.js › showEditAgencyModal): nombre, descripción, logo, banner,
// color, región y contacto. A la derecha, la cabecera de su ficha tal y como
// la pinta la app, con lo que hay en el formulario.
//
// Logo y banner van a las mismas galerías que usa la consola del dueño
// (brandLogos / brandBanners: [{ url, key, at }], 4 como mucho): así el dueño
// los ve en «Editar vista pública» y ningún archivo se queda en R2 sin dueño.

import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Switch,
  Textarea
} from '@nextui-org/react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { apiUpload } from '../../lib/api';
import { AgencyShared as S } from '../../lib/agencyApp';
import { previewAgency, profileForm, profilePatch } from '../../lib/agencyAdmin';
import { saveAgencyPatch } from '../../lib/agencyActions';
import { AgencyHero } from './AppAgencyView';
import RegionSelect from './RegionSelect';

export const BRAND_GALLERY_MAX = 4;
const MAX_BYTES = 10 * 1024 * 1024;

const KINDS = {
  logoUrl: { galleryField: 'brandLogos', imageType: 'logo', label: 'Logo', hint: 'Recomendado: 256 × 256 px, cuadrado. PNG, JPG, GIF o WEBP (máx. 10 MB).', round: true },
  bannerUrl: { galleryField: 'brandBanners', imageType: 'banner', label: 'Banner', hint: 'Fondo de su tarjeta de presentación. Recomendado: 1200 × 360 px (máx. 10 MB).' }
};

const ERRORS = {
  name: 'El nombre es obligatorio.',
  email: 'Hace falta un email de contacto válido.',
  description: `La descripción no puede pasar de ${S.AGENCY_DESCRIPTION_MAX || 2000} caracteres.`
};

// La galería de la agencia con lo que tiene puesto (aunque no esté en ella).
export function galleryWithCurrent(agency, galleryField, current) {
  const list = Array.isArray(agency?.[galleryField]) ? agency[galleryField].filter((x) => x && x.url) : [];
  if (current && !list.some((x) => x.url === current)) return [{ url: current, key: '', at: 0, outside: true }, ...list];
  return list;
}

function ImagePicker({ kind, agency, value, onChange, onUploaded }) {
  const k = KINDS[kind];
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const list = galleryWithCurrent(agency, k.galleryField, agency?.[kind]);
  const inGallery = list.filter((x) => !x.outside).length;
  const full = inGallery >= BRAND_GALLERY_MAX;

  async function pick(ev) {
    const file = ev.target.files && ev.target.files[0];
    ev.target.value = '';
    if (!file) return;
    setError('');
    if (!/^image\/(png|jpe?g|gif|webp)$/i.test(file.type || '')) { setError('Usa una imagen PNG, JPG, GIF o WEBP.'); return; }
    if (file.size > MAX_BYTES) { setError('La imagen pesa demasiado (máx. 10 MB).'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file, file.name);
      fd.append('imageType', k.imageType);
      fd.append('agencyTier', S.agencyPlan(agency).id);
      const res = await apiUpload(`/api/files/agency/${encodeURIComponent(agency.id)}/upload`, fd);
      if (!res?.url) throw new Error(res?.error || 'El servidor no ha devuelto la imagen.');
      const gallery = [
        ...(Array.isArray(agency[k.galleryField]) ? agency[k.galleryField] : []),
        { url: res.url, key: res.key || '', at: Date.now() }
      ].map((it) => ({ url: it.url, key: it.key || '', at: it.at || Date.now() }));
      // Se apunta en cuanto se sube, aunque luego se cierre sin guardar.
      await updateDoc(doc(db, 'agencies', agency.id), { [k.galleryField]: gallery });
      onUploaded?.(k.galleryField, gallery);
      onChange(res.url);
    } catch (e) {
      setError(e.message || 'No se ha podido subir.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-1.5">
      <p className="text-sm">{k.label}</p>
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => onChange('')}
          className={`flex h-14 items-center justify-center rounded-medium border-2 px-3 text-xs ${value ? 'border-default-200 text-default-500' : 'border-primary text-primary'}`}
        >
          Sin {k.label.toLowerCase()}
        </button>
        {list.map((it) => (
          <button
            type="button"
            key={it.url}
            onClick={() => onChange(it.url)}
            title={it.outside ? 'La que tiene ahora (no está en su galería)' : 'De su galería'}
            className={`relative h-14 overflow-hidden border-2 ${k.round ? 'w-14 rounded-full' : 'w-40 rounded-medium'} ${value === it.url ? 'border-primary' : 'border-default-200'}`}
          >
            <img src={it.url} alt="" className="h-full w-full object-cover" />
          </button>
        ))}
        <label className={`flex h-14 cursor-pointer items-center justify-center rounded-medium border-2 border-dashed border-default-300 px-3 text-xs ${busy || full ? 'pointer-events-none opacity-50' : ''}`}>
          {busy ? 'Subiendo…' : '+ Subir'}
          <input type="file" accept="image/png,image/jpeg,image/gif,image/webp" className="hidden" onChange={pick} disabled={busy || full} />
        </label>
      </div>
      <p className="text-xs text-default-500">
        {full ? `Su galería está llena (${BRAND_GALLERY_MAX}): elige una de las suyas o que borre alguna desde «Editar vista pública».` : k.hint}
      </p>
      {error && <p className="text-xs text-danger-500">{error}</p>}
    </div>
  );
}

export default function AgencyProfileEditor({ isOpen, onClose, agency, creators, onSaved }) {
  const [form, setForm] = useState(null);
  const [local, setLocal] = useState(agency);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen && agency) {
      setForm(profileForm(agency));
      setLocal(agency);
      setError('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const draft = useMemo(() => (form && local ? previewAgency(local, form) : null), [form, local]);

  if (!agency) return null;
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const max = S.AGENCY_DESCRIPTION_MAX || 2000;

  async function save() {
    const patch = profilePatch(S, local, form);
    if (patch.error) { setError(ERRORS[patch.error]); return; }
    setSaving(true);
    setError('');
    try {
      await saveAgencyPatch(agency.id, patch);
      onSaved?.('Ficha guardada.');
      onClose();
    } catch (e) {
      setError(e.message || 'No se ha guardado.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={saving ? undefined : onClose} size="5xl" scrollBehavior="inside">
      <ModalContent>
        <ModalHeader className="flex flex-col gap-0.5">
          <span>Editar ficha</span>
          <span className="text-xs font-normal text-default-500">{agency.name || agency.id} · {agency.id}</span>
        </ModalHeader>
        <ModalBody>
          {form && (
            <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
              <div className="flex flex-col gap-3">
                <Input size="sm" label="Nombre" isRequired value={form.name} onValueChange={(v) => set({ name: v })} />
                <Textarea
                  size="sm"
                  label="Descripción"
                  minRows={3}
                  maxRows={8}
                  value={form.description}
                  onValueChange={(v) => set({ description: v })}
                  description={`${form.description.length} / ${max}. La ficha pública enseña el mensaje de presentación del dueño y, si no lo tiene, esta descripción.`}
                  isInvalid={form.description.length > max}
                />
                <ImagePicker
                  kind="logoUrl"
                  agency={local}
                  value={form.logoUrl}
                  onChange={(url) => set({ logoUrl: url })}
                  onUploaded={(field, list) => setLocal((a) => ({ ...a, [field]: list }))}
                />
                <ImagePicker
                  kind="bannerUrl"
                  agency={local}
                  value={form.bannerUrl}
                  onChange={(url) => set({ bannerUrl: url })}
                  onUploaded={(field, list) => setLocal((a) => ({ ...a, [field]: list }))}
                />
                <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_120px]">
                  <RegionSelect value={form.region} onChange={(v) => set({ region: v })} />
                  <label className="flex flex-col gap-1 text-xs text-default-500">
                    Color
                    <input
                      type="color"
                      value={form.color}
                      onChange={(e) => set({ color: e.target.value })}
                      className="h-9 w-full cursor-pointer rounded-medium border border-default-200 bg-transparent"
                    />
                  </label>
                </div>
                <Input
                  size="sm"
                  type="email"
                  label="Email de contacto"
                  isRequired
                  value={form.contactEmail}
                  onValueChange={(v) => set({ contactEmail: v })}
                />
                <Switch size="sm" isSelected={form.contactEmailHidden} onValueChange={(v) => set({ contactEmailHidden: v })}>
                  Ocultar el email a sus creadores
                </Switch>
                <p className="-mt-2 text-xs text-default-500">
                  {Array.isArray(local?.contactMethods)
                    ? 'Su dueño ya ha elegido sus formas de contacto en la consola: lo de aquí (email oculto, teléfono, web, Discord) no cambia lo que ven sus creadores.'
                    : 'El email nunca sale en la ficha pública del Explorador; esto lo quita también de «Contactar» en la vista de sus creadores.'}
                </p>
                <div className="grid gap-3 sm:grid-cols-3">
                  <Input size="sm" label="Teléfono" value={form.contactPhone} onValueChange={(v) => set({ contactPhone: v })} />
                  <Input size="sm" label="Web" value={form.website} onValueChange={(v) => set({ website: v })} />
                  <Input size="sm" label="Discord" value={form.discord} onValueChange={(v) => set({ discord: v })} />
                </div>
              </div>

              <aside className="flex flex-col gap-2 lg:sticky lg:top-0 lg:self-start">
                <p className="text-xs uppercase tracking-wide text-default-500">Así la ven sus creadores</p>
                <AgencyHero agency={draft} creators={creators} />
                <p className="text-xs text-default-500">
                  Frase, «Qué ofrece», hitos y formas de contacto los edita el dueño en «Editar vista pública».
                </p>
              </aside>
            </div>
          )}
          {error && <p className="text-sm text-danger-500">{error}</p>}
        </ModalBody>
        <ModalFooter>
          <Button variant="flat" onPress={onClose} isDisabled={saving}>Cancelar</Button>
          <Button color="primary" onPress={save} isLoading={saving} isDisabled={!form}>Guardar</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
